import React, { useEffect, useState } from 'react';
import { marketingApi } from '../app/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Mail, CheckCircle2 } from 'lucide-react';
import { logger } from '../lib/logger';
import { getSessionMarker, getAbVariant } from '../lib/growth';

export const NewsletterForm = ({ source = 'unspecified', compact = false }) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError('Enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      await marketingApi.subscribe({
        email: email.trim().toLowerCase(),
        source,
        variant: getAbVariant('hero-cta', ['A', 'B']),
      });
      setDone(true);
    } catch (err) {
      logger.error('subscribe failed', err);
      setError(err.message || 'Could not subscribe. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className={`flex items-center gap-2 text-sm ${compact ? 'text-white/80' : 'text-foreground/80'}`} data-testid={`newsletter-success-${source}`}>
        <CheckCircle2 className="h-4 w-4 text-[#4FBCC7]" />
        <span>Thanks — you're on the list.</span>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="w-full" data-testid={`newsletter-form-${source}`}>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${compact ? 'text-white/40' : 'text-foreground/40'}`} />
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            disabled={submitting}
            aria-label="Email address"
            data-testid={`newsletter-email-${source}`}
            className={`pl-9 h-11 ${compact ? 'bg-white/10 border-white/20 text-white placeholder:text-white/40 focus-visible:ring-[#4FBCC7]' : ''}`}
          />
        </div>
        <Button
          type="submit"
          disabled={submitting}
          className={`h-11 font-semibold ${compact ? 'bg-[#4FBCC7] hover:bg-[#3aa6b1] text-[#0a0d0e]' : 'bg-[#004C54] hover:bg-[#003a40] text-white'}`}
          data-testid={`newsletter-submit-${source}`}
        >
          {submitting ? 'Joining…' : 'Get tips'}
        </Button>
      </div>
      {error && (
        <p className={`text-xs mt-1.5 ${compact ? 'text-red-300' : 'text-destructive'}`} data-testid={`newsletter-error-${source}`}>
          {error}
        </p>
      )}
      <p className={`text-[11px] mt-1.5 ${compact ? 'text-white/40' : 'text-foreground/50'}`}>
        Mental performance tips. One short email a week. Unsubscribe anytime.
      </p>
    </form>
  );
};

export const NewsletterStickyBanner = () => {
  const [visible, setVisible] = useState(false);
  const [closed, setClosed] = useState(false);
  const sessionMarker = getSessionMarker();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Friction-reduction policy: only show once per session AND only after
    // meaningful scroll engagement. Persisted dismissals still take priority.
    let dismissed = false;
    let sessionShown = false;
    try {
      dismissed = window.localStorage.getItem('apex_nl_dismissed') === '1';
      sessionShown = window.sessionStorage.getItem('apex_nl_session_shown') === '1';
    } catch (err) {
      logger.debug('newsletter visibility check failed', err);
    }
    if (dismissed || sessionShown) return;

    const SCROLL_THRESHOLD = 1800; // ~2 viewport heights — confirms real engagement

    const showOnce = () => {
      setVisible(true);
      try { window.sessionStorage.setItem('apex_nl_session_shown', '1'); }
      catch (err) { logger.debug('newsletter session mark failed', err); }
      window.removeEventListener('scroll', onScroll);
    };

    const onScroll = () => {
      if (window.scrollY > SCROLL_THRESHOLD) showOnce();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const close = () => {
    setClosed(true);
    try { window.localStorage.setItem('apex_nl_dismissed', '1'); }
    catch (err) { logger.debug('newsletter dismiss persist failed', err); }
  };

  if (!visible || closed) return null;

  return (
    <div
      key={sessionMarker}
      className="fixed bottom-0 inset-x-0 z-40 px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none"
      data-testid="newsletter-sticky-banner"
    >
      <div className="pointer-events-auto max-w-4xl mx-auto rounded-2xl bg-gradient-to-r from-[#0a0d0e] via-[#0f1416] to-[#0a0d0e] border border-white/10 shadow-2xl p-4 sm:p-5 backdrop-blur-md">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-5">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#004C54]/30 border border-[#004C54]/40 flex items-center justify-center shrink-0">
              <Mail className="h-4 w-4 text-[#4FBCC7]" />
            </div>
            <div className="min-w-0">
              <div className="font-heading text-[15px] font-bold text-white leading-tight">Train smarter every week</div>
              <div className="text-[12px] text-white/60 mt-0.5">Free mental performance tips from Coach Lewis. No spam.</div>
            </div>
          </div>
          <div className="flex items-start gap-2 sm:items-center sm:max-w-md sm:w-full">
            <div className="flex-1">
              <NewsletterForm source="sticky" compact />
            </div>
            <button
              type="button"
              onClick={close}
              className="shrink-0 h-9 px-2 text-white/40 hover:text-white text-xs underline-offset-2 hover:underline"
              data-testid="newsletter-dismiss"
              aria-label="Dismiss newsletter banner"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsletterForm;
